/**
 * Shared header + footer used by all public product marketing pages.
 * Pass `activePage` matching one of the productNav `name` values to highlight it.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Shield, Globe, Network, Twitter, Users, Plane,
  ChevronDown, Search, Building2, Scale, Newspaper,
  Briefcase, Banknote, Landmark, Target, Database, Wallet
} from 'lucide-react'
import { Button } from '@/components/ui/button'

export const industryNav = [
  { slug: 'government',           name: 'Government & Defense',       icon: Building2 },
  { slug: 'law-enforcement',      name: 'Law Enforcement',            icon: Shield },
  { slug: 'financial-services',   name: 'Financial Services',         icon: Landmark },
  { slug: 'cybersecurity',        name: 'Cybersecurity Teams & SOCs', icon: Target },
  { slug: 'legal',                name: 'Legal & Compliance',         icon: Scale },
  { slug: 'journalism',           name: 'Journalism & Media',         icon: Newspaper },
  { slug: 'corporate-risk',       name: 'Corporate Risk',             icon: Briefcase },
  { slug: 'aml',                  name: 'Anti-Money Laundering',      icon: Banknote },
  { slug: 'private-investigators',name: 'Private Investigators',      icon: Search },
]

export const productNav = [
  { name: 'BreachINT',       desc: 'Breach & credential leak intel', icon: Database, to: '/breachint' },
  { name: 'Sycek Profiler',  desc: 'Identity intelligence',       icon: Users,    to: '/sycek-profiler' },
  { name: 'WalletINT',       desc: 'Crypto wallet AML screening', icon: Wallet,   to: '/walletint' },
  { name: 'AML Profiler',    desc: 'Customer screening & EDD',    icon: Banknote, to: '/aml-profiler' },
  { name: 'SOCINT',          desc: 'Cross-platform identity graph', icon: Search, to: '/socint' },
  { name: 'Social Stream',   desc: 'Multi-platform monitoring',   icon: Network,  to: '/social-stream-product' },
  { name: 'GEOINT',          desc: 'Satellite & geospatial',      icon: Globe,    to: '/geoint' },
  { name: 'X Investigation', desc: 'Twitter deep-dive',           icon: Twitter,  to: '/x-investigation-product' },
  { name: 'Air & Maritime',  desc: 'Flight & vessel tracking',    icon: Plane,    to: '/air-maritime' },
]

interface Props {
  activePage?: string
}

export function ProductPageNav({ activePage }: Props) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)

  return (
    <header className="sticky top-0 z-50 border-b border-white/[0.07] bg-[#070b0e]/95 backdrop-blur-md">
      <div className="container mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity flex-shrink-0">
          <div className="w-9 h-9 rounded-lg overflow-hidden">
            <img src="/logo.jpeg" alt="Sycek OSINT" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-base font-bold text-white tracking-tight leading-none">Sycek</h1>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest leading-none mt-0.5">Intelligence Platform</p>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {/* Industries dropdown */}
          <div className="relative"
            onMouseEnter={() => setOpenDropdown('industries')}
            onMouseLeave={() => setOpenDropdown(null)}>
            <button className="flex items-center gap-1.5 px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-md hover:bg-white/[0.06]">
              Industries <ChevronDown className={`w-3.5 h-3.5 opacity-60 transition-transform ${openDropdown === 'industries' ? 'rotate-180' : ''}`} />
            </button>
            {openDropdown === 'industries' && (
              <div className="absolute top-full left-0 mt-1 w-72 bg-[#0d1117] border border-white/[0.09] rounded-xl shadow-2xl shadow-black/70 p-2 z-50">
                {industryNav.map(ind => (
                  <Link key={ind.slug} to={`/industries/${ind.slug}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.05] text-slate-400 hover:text-white transition-colors group">
                    <ind.icon className="w-4 h-4 text-slate-600 group-hover:text-green-400 transition-colors flex-shrink-0" />
                    <span className="text-sm">{ind.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Products dropdown */}
          <div className="relative"
            onMouseEnter={() => setOpenDropdown('products')}
            onMouseLeave={() => setOpenDropdown(null)}>
            <button className={`flex items-center gap-1.5 px-4 py-2 text-sm transition-colors rounded-md ${activePage ? 'text-green-400 bg-green-500/[0.08]' : 'text-slate-300 hover:text-white hover:bg-white/[0.06]'}`}>
              Products <ChevronDown className={`w-3.5 h-3.5 opacity-60 transition-transform ${openDropdown === 'products' ? 'rotate-180' : ''}`} />
            </button>
            {openDropdown === 'products' && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-[#0d1117] border border-white/[0.09] rounded-xl shadow-2xl shadow-black/70 p-2 z-50">
                {productNav.map(p => {
                  const isActive = p.name === activePage
                  return (
                    <Link key={p.name} to={p.to}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.05] transition-colors group ${isActive ? 'bg-green-500/[0.07] text-green-400' : 'text-slate-400 hover:text-white'}`}>
                      <p.icon className={`w-4 h-4 flex-shrink-0 transition-colors ${isActive ? 'text-green-400' : 'text-slate-600 group-hover:text-green-400'}`} />
                      <div>
                        <div className="text-sm">{p.name}</div>
                        <div className="text-[11px] text-slate-600">{p.desc}</div>
                      </div>
                    </Link>
                  )
                })}
              </div>
            )}
          </div>

          <Link to="/use-cases" className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-md hover:bg-white/[0.06]">Use Cases</Link>
          <Link to="/monitor" className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-md hover:bg-white/[0.06]">Live Monitor</Link>
          <Link to="/pricing" className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-md hover:bg-white/[0.06]">Pricing</Link>
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/login">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-white/[0.06]">Sign In</Button>
          </Link>
          <Link to="/register">
            <Button size="sm" className="bg-green-500 hover:bg-green-600 text-black font-semibold shadow-lg shadow-green-500/30">Get Started</Button>
          </Link>
        </div>
      </div>
    </header>
  )
}

export function ProductPageFooter() {
  return (
    <footer className="relative z-10 border-t border-white/[0.06] py-10 px-6">
      <div className="container mx-auto max-w-5xl flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-600">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded overflow-hidden">
            <img src="/logo.jpeg" alt="Sycek" className="w-full h-full object-cover" />
          </div>
          <span>Sycek Intelligence Platform</span>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/privacy-policy" className="hover:text-slate-400 transition-colors">Privacy</Link>
          <Link to="/terms-of-service" className="hover:text-slate-400 transition-colors">Terms</Link>
          <Link to="/compliance" className="hover:text-slate-400 transition-colors">Compliance</Link>
          <Link to="/pricing" className="hover:text-slate-400 transition-colors">Pricing</Link>
        </div>
        <div className="text-xs">&copy; {new Date().getFullYear()} Sycek. All rights reserved.</div>
      </div>
    </footer>
  )
}
